import alertCaller from '@core/app/actions/alert-caller';
import type { ConvertResultType } from '@core/app/actions/beambox/font-funcs';
import fontFuncs, { ConvertResult } from '@core/app/actions/beambox/font-funcs';
import alertConstants from '@core/app/constants/alert-constants';
import { changeAttribute } from '@core/app/svgedit/history/changeAttribute';
import { BatchCommand, InsertElementCommand } from '@core/app/svgedit/history/history';
import undoManager from '@core/app/svgedit/history/undoManager';
import { handleHistoryActionOptions } from '@core/app/svgedit/history/utils/handleHistoryActionOptions';
import { deleteElements } from '@core/app/svgedit/operations/delete';
import disassembleUse from '@core/app/svgedit/operations/disassembleUse';
import selectionManager from '@core/app/svgedit/selection';
import { renderAllParamsLabels } from '@core/app/svgedit/text/paramsLabel';
import textActions from '@core/app/svgedit/text/textactions';
import textedit from '@core/app/svgedit/text/textedit';
import type { HistoryActionOptions, IBatchCommand, ICommand } from '@core/interfaces/IHistory';
import type ISVGCanvas from '@core/interfaces/ISVGCanvas';

import alertConfig from '../api/alert-config';
import i18n from '../i18n';
import { getSVGAsync } from '../svg-editor-helper';

type ConvertToPathResult = {
  bbox: DOMRect;
  command: ICommand | undefined;
  path?: SVGPathElement;
};

type ConvertTextToPathResult = ConvertToPathResult & {
  status: ConvertResultType;
};

let svgCanvas: ISVGCanvas;
let svgedit: any;

getSVGAsync(({ Canvas, Edit }) => {
  svgCanvas = Canvas;
  svgedit = Edit;
});

export const convertSvgToPath = (
  element: SVGElement,
  { isToSelect = false, ...historyOptions }: HistoryActionOptions & { isToSelect?: boolean } = {},
): ConvertToPathResult => {
  const { cmd, path } = svgCanvas.convertToPath(element, true);

  if (isToSelect) {
    selectionManager.selectOnly([path]);
  }

  handleHistoryActionOptions(cmd, historyOptions);

  return { bbox: path.getBBox(), command: historyOptions.parentCmd || cmd, path };
};

export const convertTextToPath = async (
  element: SVGElement,
  {
    isToSelect = false,
    pathPerChar = false,
    weldingTexts = false,
    ...historyOptions
  }: HistoryActionOptions & {
    isToSelect?: boolean;
    pathPerChar?: boolean;
    weldingTexts?: boolean;
  },
): Promise<ConvertTextToPathResult> => {
  if (textActions.isEditing) textActions.toSelectMode();

  const { command, path, status } = await fontFuncs.convertTextToPath(element, {
    isSubCommand: true,
    pathPerChar,
    weldingTexts,
  });

  if (command) {
    handleHistoryActionOptions(command, historyOptions);
  }

  if (path && isToSelect) {
    selectionManager.selectOnly([path]);
  }

  return {
    bbox: path?.getBBox()!,
    command: historyOptions.parentCmd || command || undefined,
    path: path || undefined,
    status,
  };
};

export const convertTempGroupToPath = async ({
  element,
  isToSelect = false,
  weldingTexts = false,
}: {
  element: SVGElement;
  isToSelect?: boolean;
  weldingTexts?: boolean;
}): Promise<void> => {
  const elements = selectionManager.ungroupTempGroup(element);
  const batchCommand = new BatchCommand('Convert to Path');
  const paths: SVGElement[] = [];

  for (const el of elements) {
    if (el.nodeName === 'g' && el.getAttribute('data-textpath-g')) {
      const { group } = await convertTextOnPathToPath(el, {
        parentCmd: batchCommand,
        weldingTexts,
      });

      if (group) paths.push(group);
    } else if (el.nodeName === 'text') {
      const { path } = await convertTextToPath(el, {
        isToSelect: false,
        parentCmd: batchCommand,
        weldingTexts,
      });

      if (path) paths.push(path);
    } else {
      const { path } = convertSvgToPath(el, {
        isToSelect: false,
        parentCmd: batchCommand,
      });

      if (path) paths.push(path);
    }
  }

  undoManager.addCommandToHistory(batchCommand);

  if (isToSelect && paths.length > 0) {
    selectionManager.multiSelect(paths);
  }
};

export const convertTextOnPathToPath = async (
  element: SVGElement,
  {
    weldingTexts = false,
    ...historyOptions
  }: HistoryActionOptions & {
    weldingTexts?: boolean;
  } = {},
): Promise<ConvertToPathResult & { group: SVGGElement }> => {
  const cmd = new BatchCommand('Convert Text on Path to Path');

  const textElement = element.querySelector('text');

  if (textActions.isEditing) textActions.toSelectMode();

  selectionManager.clearSelection();

  const { command, path } = await fontFuncs.convertTextToPath(textElement!, { isSubCommand: true, weldingTexts });
  const group = element as SVGGElement;

  if (!path) {
    return { bbox: group.getBBox(), command: undefined, group };
  }

  if (command) cmd.addSubCommand(command);

  // Keep the original group so its transform (e.g. rotation) is preserved;
  // the converted path is inserted in place of the text, so no regrouping is needed.

  const changeAttrCmd = changeAttribute(group, { 'data-ratiofixed': 'true', 'data-textpath-g': undefined });

  if (changeAttrCmd) cmd.addSubCommand(changeAttrCmd);

  selectionManager.selectOnly([group]);

  handleHistoryActionOptions(cmd, historyOptions);

  return {
    bbox: path.getBBox(),
    command: historyOptions.parentCmd ?? cmd,
    group,
    path,
  };
};

export const convertUseToPath = async ({
  element,
  isToSelect = false,
}: {
  element: SVGElement;
  isToSelect?: boolean;
}): Promise<ConvertToPathResult> => {
  const command = (await disassembleUse([element], {
    addToHistory: false,
    showProgress: false,
    skipConfirm: true,
  })) as BatchCommand;

  const group = selectionManager.getSelectedElements()[0];

  if (!(group instanceof SVGGElement)) {
    return convertSvgToPath(group, { parentCmd: command });
  }

  const head = group.childNodes[0] as SVGPathElement;
  const pathData = Array.of<string>();
  const toRemove = Array.of<SVGElement>();

  group.childNodes.forEach((child, index) => {
    pathData.push((child as SVGPathElement).getAttribute('d')!);

    if (index !== 0) toRemove.push(child as SVGElement);
  });

  command.addSubCommand(deleteElements(toRemove, true));

  head.setAttribute('d', pathData.join(' '));
  head.removeAttribute('data-next-sibling');

  if (isToSelect) {
    selectionManager.selectOnly([head]);
  }

  return { bbox: head.getBBox(), command, path: head };
};

export const generateImageRect = (element?: SVGImageElement): { command?: IBatchCommand; rect?: SVGElement } => {
  if (!element) {
    return { command: undefined, rect: undefined };
  }

  const batchCommand = new BatchCommand('Generate Image Rect');
  const bbox = element.getBBox();
  const rotation = svgedit.utilities.getRotationAngle(element);
  const rect = svgCanvas.addSvgElementFromJson({
    attr: {
      fill: 'none',
      height: bbox.height,
      id: svgCanvas.getNextId(),
      rotation,
      stroke: '#000000',
      'stroke-width': 1,
      'vector-effect': 'non-scaling-stroke',
      width: bbox.width,
      x: bbox.x,
      y: bbox.y,
    },
    element: 'rect',
  });

  batchCommand.addSubCommand(new InsertElementCommand(rect));

  return { command: batchCommand, rect };
};

/**
 * Converts all <text> and text-on-path elements on the canvas to paths.
 * @returns A promise that resolves to a function that can revert the conversion.
 */
export const convertAllTextToPath = async ({ pathPerChar = false }: { pathPerChar?: boolean } = {}): Promise<{
  revert: () => void;
  success: boolean;
}> => {
  // 1. Create a master command to record all changes.
  const batchCmd = new BatchCommand('Convert All Text to Path');

  // Temporary trigger for refreshing parameter labels.
  // TODO: Move this to the common export flow.
  renderAllParamsLabels({ parentCmd: batchCmd });

  const texts = [
    ...document.querySelectorAll('#svgcontent g.layer:not([display="none"]) text'),
    ...document.querySelectorAll('#svg_defs text'),
  ] as SVGElement[];

  let isAnyFontUnsupported = false;

  /**
   * Reverts the conversion from text to paths.
   */
  const revert = () => {
    // The unapply method reverses the command. It requires an object with a
    // renderText function, which we can get from the editor's textActions.
    batchCmd.unapply({
      handleHistoryEvent: () => {},
      renderText: textedit.renderText,
    });
  };

  for (const element of texts) {
    const { status } = await convertTextToPath(element, { parentCmd: batchCmd, pathPerChar });

    // cancelling midway leaves the already-converted elements behind, so revert what batchCmd holds
    if (status === ConvertResult.CANCEL_OPERATION) {
      return { revert, success: false };
    }

    if (status === ConvertResult.UNSUPPORT) isAnyFontUnsupported = true;
  }

  if (isAnyFontUnsupported && !alertConfig.read('skip_check_thumbnail_warning')) {
    await new Promise<void>((resolve) => {
      alertCaller.popUp({
        callbacks: () => resolve(),
        checkbox: {
          callbacks: () => {
            alertConfig.write('skip_check_thumbnail_warning', true);
            resolve();
          },
          text: i18n.lang.alert.dont_show_again,
        },
        message: i18n.lang.beambox.object_panels.text_to_path.check_thumbnail_warning,
        type: alertConstants.SHOW_POPUP_WARNING,
      });
    });
  }

  return {
    revert,
    success: true,
  };
};

/** Warning: This is a dispatcher only. Each converter has its own options and return type. */
export const dispatchConvertToPath = async (
  element: SVGElement,
  {
    isToSelect,
    weldingTexts,
    ...historyOptions
  }: HistoryActionOptions & {
    isToSelect?: boolean;
    weldingTexts?: boolean;
  },
) => {
  const tagName = element.tagName.toLowerCase();

  if (tagName === 'text') {
    return convertTextToPath(element, { isToSelect, weldingTexts, ...historyOptions });
  } else if (tagName === 'use') {
    // Note: returns command without adding to history
    return convertUseToPath({ element, isToSelect });
  } else if (tagName === 'g') {
    if (element.getAttribute('data-tempgroup') === 'true') {
      // Note: adds command to history; returns void instead of ConvertToPathResult
      return convertTempGroupToPath({ element, isToSelect, weldingTexts });
    } else if (element.getAttribute('data-textpath-g')) {
      // Note: selects resulting path with group
      return convertTextOnPathToPath(element, { weldingTexts, ...historyOptions });
    }
  }

  return convertSvgToPath(element, { isToSelect, ...historyOptions });
};
